import {
  type AlarmContactId as _AlarmContactId,
  type CameraGroupId as _CameraGroupId,
  type CameraLinkId as _CameraLinkId,
  type CHCameraId as _CHCameraId,
  type CHCloudStoragePackageId as _CHCloudStoragePackageId,
  type CHStreamCloudRetentionId as _CHStreamCloudRetentionId,
  type CHStreamId as _CHStreamId,
  type CloudId as _CloudId,
  type CustomerId as _CustomerId,
  type DeviceId as _DeviceId,
  type DoorId as _DoorId,
  type EventId as _EventId,
  type ResellerId as _ResellerId,
  type ScheduleId as _ScheduleId,
  type SiteId as _SiteId,
  type UserId as _UserId,
  type VerifEyeGroupId as _VerifEyeGroupId,
  type VMSCameraId as _VMSCameraId,
  type VMSStreamId as _VMSStreamId,
  type AlarmConfigDto,
  type AlarmContactDto,
  type components,
  type SiteGroupId,
  type UserRoleId,
} from '@solinkcorp/callhome';
import type { ConnectSettings } from '@solinkcorp/interlink-shared-types';
import { type WeeklySchedule } from '@solinkcorp/solink-schedule';
import type { Except, ValueOf } from 'type-fest';

// VerifEye Configuration Constants
export interface AlarmZone {
  id: string;
  name: string;
}

export const ALARM_PRIORITY = {
  HIGH: 'ALARM_PRIORITY_1',
  MEDIUM: 'ALARM_PRIORITY_2',
  LOW: 'ALARM_PRIORITY_3',
} as const;

export const PRIORITY = {
  HIGH: 'HIGH',
  MEDIUM: 'MEDIUM',
  LOW: 'LOW',
} as const;

// Branded IDs — import directly from @solinkcorp/callhome instead

/** @deprecated Import `AlarmContactId` from `@solinkcorp/callhome` */
export type AlarmContactId = _AlarmContactId;
/** @deprecated Import `CameraGroupId` from `@solinkcorp/callhome` */
export type CameraGroupId = _CameraGroupId;
/** @deprecated Import `CameraLinkId` from `@solinkcorp/callhome` */
export type CameraLinkId = _CameraLinkId;
/** @deprecated Import `CHCameraId` from `@solinkcorp/callhome` */
export type CHCameraId = _CHCameraId;
/** @deprecated Import `CHCloudStoragePackageId` from `@solinkcorp/callhome` */
export type CHCloudStoragePackageId = _CHCloudStoragePackageId;
/** @deprecated Import `CHStreamCloudRetentionId` from `@solinkcorp/callhome` */
export type CHStreamCloudRetentionId = _CHStreamCloudRetentionId;
/** @deprecated Import `CHStreamId` from `@solinkcorp/callhome` */
export type CHStreamId = _CHStreamId;
/** @deprecated Import `CloudId` from `@solinkcorp/callhome` */
export type CloudId = _CloudId;
/** @deprecated Import `CustomerId` from `@solinkcorp/callhome` */
export type CustomerId = _CustomerId;
/** @deprecated Import `DeviceId` from `@solinkcorp/callhome` */
export type DeviceId = _DeviceId;
/** @deprecated Import `DoorId` from `@solinkcorp/callhome` */
export type DoorId = _DoorId;
/** @deprecated Import `EventId` from `@solinkcorp/callhome` */
export type EventId = _EventId;
/** @deprecated Import `ResellerId` from `@solinkcorp/callhome` */
export type ResellerId = _ResellerId;
/** @deprecated Import `ScheduleId` from `@solinkcorp/callhome` */
export type ScheduleId = _ScheduleId;
/** @deprecated Import `SiteId` from `@solinkcorp/callhome` */
export type SiteId = _SiteId;
/** @deprecated Import `UserId` from `@solinkcorp/callhome` */
export type UserId = _UserId;
/** @deprecated Import `VerifEyeGroupId` from `@solinkcorp/callhome` */
export type VerifEyeGroupId = _VerifEyeGroupId;
/** @deprecated Import `VMSCameraId` from `@solinkcorp/callhome` */
export type VMSCameraId = _VMSCameraId;
/** @deprecated Import `VMSStreamId` from `@solinkcorp/callhome` */
export type VMSStreamId = _VMSStreamId;

export type UserIdentityType = 'AUTH0_USER' | 'GUEST_USER' | 'API_KEY';

export type LocaleName = 'en-CA' | 'en-US' | 'en-GB' | 'fr-FR' | 'es-ES';

export type SupportedSpeaker =
  | 'speaker0'
  | 'speaker1'
  | 'speaker2'
  | 'speaker3'
  | 'speaker4'
  | 'speaker5'
  | 'speaker6'
  | 'speaker7'
  | 'speaker8'
  | 'speaker9';

export const DEVICE_TYPE = {
  UNKNOWN: 'unknown',
  QNAP: 'qnap',
  UBUNTU: 'ubuntu',
  HIK: 'hik',
  ANDROID: 'android',
  DATA: 'data',
  CONTAINER: 'container',
  CAMERA: 'camera',
} as const;

export const GENERAL_SCHEMA_ID = 'GeneralVerificationSchema20240612';

export interface VerificationClaim {
  type: 'verification-claim';
  claimedBy: string;
  updatedTime: number;
}

export interface VerificationPrompt {
  type: 'verification-prompt';
  updatedBy: string;
  updatedTime: number;
}

export type VerificationDetails = VerificationClaim | VerificationPrompt;

export interface VideoWallLayoutItem {
  locationId?: SiteId;
  cameraId?: CHCameraId;
  key: string;
  page: number;
  gridX: number;
  gridY: number;
  size: number;
}

export interface CameraGroupLayout {
  gridWidth: number;
  gridHeight: number;
  cameras: VideoWallLayoutItem[];
}

// https://github.com/SolinkCorp/call-home-server/blob/develop/src/core/enums/alarm-mode.enum.ts
export enum AlarmMode {
  ACTIVE = 'ACTIVE',
  DISABLED = 'DISABLED',
  TEST = 'TEST',
}

export type AlarmProviderType = AlarmConfigDto['providerType'];

export type AlarmContactType = AlarmContactDto['type'];

export enum AlarmConfigState {
  DISARMED_FOREVER = 'DISARMED_FOREVER',
  SCHEDULE = 'SCHEDULE',
  ARMED_FOREVER = 'ARMED_FOREVER',
}

export type DisarmMethod = 'DTMF' | 'PINPAD';

export interface Point {
  x: number;
  y: number;
}

export interface Broadcast {
  profiles: Record<string, Profile>;
  cameraProfiles: Record<string, string>;
}

export interface Profile {
  speakers: string[];
  triggers: string[];
  audioLoops: number;
  audioSource: string;
  conferenceTimeLimit: number;
}

export interface SipConfig {
  cameraSpeakerMapping: Record<string, SupportedSpeaker[]>;
  broadcast: Broadcast;
  domain?: string;
}

/** A customer-configured alarm site label (e.g. a GSOC), used to tag/triage events. */
export interface AlarmSiteLabel {
  id: string;
  name: string;
}

/** @deprecated Use `AlarmConfigDto` from `@solinkcorp/callhome` */
export interface CallhomeAlarmConfig {
  id: string;
  cloudId: string;
  resellerId: string;
  customerId: string;
  deviceId: string;
  providerType: AlarmProviderType;
  /** It's a JSON blob in Callhome. The properties inside can be anything. */
  providerSettings: {
    guestAccessEnabled?: boolean;
    isSandbox?: boolean;
    /**
     * SIP Alarm Speaker configuration
     */
    sip?: SipConfig;
    /**
     * Alarm zone definitions for A2C2 camera zone configuration.
     * The presence of this field enables the zone configuration UI.
     */
    alarmZones?: AlarmZone[];
    /**
     * Customer's alarm site label catalog (id -> display name). Configured at the
     * provider level and surfaced via the parent-merged alarm config
     * (`includeParents=true`).
     */
    alarmSiteLabels?: AlarmSiteLabel[];
    /**
     * Enables the VerifEye 4 configuration UI (VerifEyeConfigCard). When true,
     * the card is surfaced regardless of whether alarm zones are configured.
     */
    enableVerifeye4?: boolean;
  };
  mode: AlarmMode;
  note?: string;
  locationNote?: string;
  /** It's a JSON blob in Callhome. The properties inside can be anything. */
  notificationMetadata: {
    pinCameras?: string[];
    contacts?: { [key: string]: string };
    emergencyContact?: string;
    /**
     * pinDelay is a delay, in seconds.  In the disarm case, this is the delay
     * between when an alarm is triggered and when the voice call is made.
     * In the arm case, arming the alarm will be delayed by `pinDelay + ringTimeSeconds`.
     */
    pinDelay?: number;
    /**
     * In the disarm case, this is the number of seconds to allow the phone to ring.
     * In the arm case, arming the alarm will be delayed by `pinDelay + ringTimeSeconds`.
     */
    ringTimeSeconds?: number;
    /**
     * Delay (in seconds) between when alarm is triggered and when we actually
     * fire the alarm. This should be longer than pinDelay.
     */
    pinTimeout?: number;
  };
  disarmUntil: number;
  armUntil: number;
  state: AlarmConfigState;
  dtmfPinPhoneNumber?: string | null;
  dtmfPinSendDigits?: string | null;
  masonDeviceIds: string[];
  twilioPhoneNumber: string | null;
  disarmMethod: DisarmMethod | null;
  /** It's a JSON blob in Callhome. The properties inside can be anything. */
  features?: {
    /** If a location are allowed to have cameras with the outdoor tag when Noonlight is the alarm provider. */
    outdoorAlarms?: boolean;
  };
  rekognitionSettings?: {
    targetLabels?: string[] | Record<string, string[]>;
  };
  addonFeatures?: string[];
  /** Operation settings for VerifEye configuration */
  operationSettings?: {
    alarmMonitoringMode?: string;
    /** Server-managed unix seconds after which RG/STAY reverts to INTRUSION_DETECTION; 0 = no expiry. */
    modeValidUntil?: number;
  };
}

export enum CameraLinkType {
  AI_DEFINED = 'AI_DEFINED',
  USER_DEFINED = 'USER_DEFINED',
}

export type CallhomeAlarmState = Pick<CallhomeAlarmConfig, 'state' | 'armUntil' | 'disarmUntil'>;

/** @deprecated Use `CameraLinkDto` from `@solinkcorp/callhome` */
export interface CallhomeCameraLink {
  id: CameraLinkId;
  cloudId: CloudId;
  resellerId: ResellerId;
  customerId: CustomerId;
  deviceId: DeviceId;
  siteId: SiteId;

  /**
   * From the camera id
   */
  fromCameraId: CHCameraId;
  fromVmsCameraId: VMSCameraId;

  /**
   * To the camera id
   */
  toCameraId: CHCameraId;
  toVmsCameraId: VMSCameraId;

  /**
   * Region of interest
   */
  roi: string;

  /**
   * The center point
   */
  center: Point;

  /**
   * Type of camera link type
   */
  type: CameraLinkType;

  /**
   * Is this AI Camera Link enabled?
   */
  isEnabled: boolean;
}

/**
 * Per-site assignment for a customer-level alarm contact.
 * Only present on contacts in the new customer-level schema
 * (i.e. when alarmConfig.providerSettings.alarmContactMigrated is true).
 */
export interface AlarmContactSiteAssignment {
  siteId: _SiteId;
  priority: number;
  /** Whether the contact's accessCode is active for this site. Defaults to true. */
  pinEnabled: boolean;
  /** Whether the contact receives alarm notifications for this site. Defaults to true except for GUEST contacts. */
  isNotificationsEnabled: boolean;
}

/** @deprecated Use `AlarmContactDto` from `@solinkcorp/callhome` */
export interface AlarmContactEntity {
  id: AlarmContactId;
  cloudId: CloudId;
  resellerId: ResellerId;
  customerId: CustomerId;
  /**
   * Null for customer-level (new schema) contacts.
   * Non-null for legacy site-scoped contacts.
   * TODO SL-12707 Phase 3 cleanup: remove nullable once all customers are migrated
   */
  deviceId: DeviceId | null;
  /**
   * Null for customer-level (new schema) contacts.
   * Non-null for legacy site-scoped contacts.
   * TODO SL-12707 Phase 3 cleanup: remove nullable once all customers are migrated
   */
  siteId: SiteId | null;
  userId?: UserId | null;
  type: AlarmContactType;
  phoneNumber?: string | null;
  name: string;
  email?: string | null;
  languagePreference: LocaleName;
  priority: number;
  accessCode?: string | null;
  expiresAt?: number;

  /**
   * disarmForSeconds is the number of seconds to disarm the alarm for.  This
   * only applies to users of type DTMFPIN.
   */
  disarmForSeconds?: number;

  /**
   * Present on customer-level (new schema) contacts only.
   * Lists all sites this contact is assigned to, with per-site priority and pinEnabled.
   * TODO SL-12707 Phase 3 cleanup: make non-optional once all customers are migrated
   */
  siteAssignments?: AlarmContactSiteAssignment[];
}

export type CreateAlarmContactEntity = Except<
  AlarmContactEntity,
  'id' | 'cloudId' | 'resellerId' | 'customerId' | 'languagePreference' | 'deviceId' | 'siteId'
>;

export interface GeoPoint {
  lat: number;
  lng: number;
}

export type UserType = 'solink' | 'cloud' | 'reseller' | 'admin' | 'standard' | 'connect';
export type TpuStatus = 'OK' | 'UNKNOWN' | 'OK_TO_UNKNOWN_1' | 'OK_TO_UNKNOWN_2' | 'ERROR';

export interface UserDefaults {
  defaultUserFeatures: Record<string, unknown>;
  defaultUserPreferences: Record<string, unknown>;
}

export interface UserRoleDefaults {
  defaultUserFeatures: Record<string, unknown>;
  defaultUserPreferences: Record<string, unknown>;
}

export enum ScheduleType {
  MOTION_ALARM = 'MOTION_ALARM',
  OPERATING_HOURS = 'OPERATING_HOURS',
  AI_OPERATING_HOURS = 'AI_OPERATING_HOURS',
}

export enum ScheduleState {
  OFF = 'OFF',
  SCHEDULE = 'SCHEDULE',
  ALWAYS_ON = 'ALWAYS_ON',
}

/** @deprecated Use `CameraGroupDto` from `@solinkcorp/callhome` */
export interface CallhomeCameraGroup {
  id: CameraGroupId;
  type: 'STANDARD' | 'FLOOR_PLAN';
  ownerType?: string;
  name: string;
  geolocation?: GeoPoint;
  locationId?: SiteId;
  deviceIds?: DeviceId[];
  cameraIds?: CHCameraId[];
  cloudId: CloudId;
  resellerId: ResellerId;
  customerId: CustomerId;
  userId?: UserId;
  layout: CameraGroupLayout;
  floorPlans?: CallhomeFloorplan[];
}

/** @deprecated Use `ScheduleDto` from `@solinkcorp/callhome` */
export interface CallhomeScheduleEntity {
  id: ScheduleId;
  cloudId: CloudId;
  resellerId: ResellerId;
  customerId: CustomerId;
  deviceId: DeviceId;
  type: ScheduleType;
  schedule: WeeklySchedule;
  mergedSchedule?: WeeklySchedule;
  state: ScheduleState;
  muteUntil?: number;
}

/** @deprecated Use `CameraStreamDto` from `@solinkcorp/callhome` */
export type CallhomeStream = {
  id: VMSStreamId;
  name: string;
  average_bps?: number;
  bitrate?: string;
  callhomeId: CHStreamId;
  channel?: number;
  rtsp: string;
  url: string;
  cloudVMS?: {
    id: string;
    count: number;
    duration: number;
    earliestSegmentDate?: number;
    latestSegmentDate?: number;
    size?: number;
  };
  dimensions?: {
    width: number;
    height: number;
  };
  audioStats?: {
    bitrate: number;
    codec: string;
    dec: number;
    present: number;
    s_rate: number;
    transc: number;
  };
  earliestSegmentDate?: number | string;
  latestSegmentDate?: number;
  failedDeletes?: number;
  framerate?: string;
  isCloudRecordingEnabled: boolean;
  isMp4Enabled: boolean;
  lastDeleteQueryElapsedTime?: number;
  lastDeleteQueryTime?: number;
  latestThumb?: string;
  qa?: {
    brokenDuration: boolean;
    brokenSEI: boolean;
    brokenTimestamps: boolean;
  };
  quality?: string;
  resolution?: string;
  retention?: number | string;
  retentionStats?: {
    start: number;
    end: number;
    nChunks: number;
    nGaps: number;
    totalRecordedLength_ms: number;
    totalRetentionRatio: string;
    partialRetentionRatio: string;
    earliestChunkInInterval: number;
  };
  successfulDeletes?: number;
  totalSegments?: number;
  isMotionOn?: boolean;
  enableMotionlessRetention?: boolean;
  weeklyRollingAvgBitrate?: number;
  earliestDerivedSegmentDate?: number;
  latestDerivedSegmentDate?: number;
  derivedStreams?: {
    quality?: 'ad';
    level: number;
    earliestSegmentDate: number;
    latestSegmentDate: number;
  }[];
  isADEnabled?: boolean;
  targetADBitrate?: number;
};

type PlaybackMethod = 'RELAY_1' | 'RELAY_2' | 'WEBRTC' | 'HLS_LOW_LATENCY';

/** @deprecated Use `CameraDto` from `@solinkcorp/callhome` */
export interface BasicCallhomeCamera {
  id: CHCameraId;
  name: string;
  siteId: SiteId;
  cameraId: string;
  deviceId: string;
  status: string;
  thumbnail?: string;
  cameraTagIds: string[];
  isMotionAlarmEnabled?: boolean;
  geoLocation?: GeoPoint;
  deviceUrl: string | null;
  deviceLocalUrl: string | null;
}

/** @deprecated Use `CameraDto` from `@solinkcorp/callhome` */
export interface CallhomeCamera {
  id: CHCameraId;
  siteId: SiteId;
  cloudId: CloudId;
  resellerId: ResellerId;
  customerId: CustomerId;
  deviceId: DeviceId;
  cameraId: VMSCameraId;
  name: string;
  overrideName?: string | null;
  vmsName?: string;
  status: string;
  streams: CallhomeStream[];
  thumbnail?: string;
  access: string;
  ip?: string;
  isMotionEnabled?: boolean;
  manufacturer?: string;
  model?: string;
  firmwareVersion?: string;
  serialNumber?: string;
  hardwareId?: string;
  identifiedBy?: string;
  geoLocation?: GeoPoint;
  isMotionAlarmEnabled?: boolean;
  ai?: Record<string, any>;
  alarms?: Record<string, any>;
  audioParams?: any;
  annotations?: any[];
  isUnplugged?: boolean;
  isOutOfFocus?: boolean;
  motionAlarmScheduleId: string | null;
  motionAlarmSchedule?: CallhomeScheduleEntity;
  motionParams?: {
    enabled: boolean;
    roi: string;
    sensitivity: number;
    threshold: number;
    eventThreshold?: 1 | 2 | 3;
  };
  '360Settings'?: Record<string, any>;
  createdAt?: Date;
  segmentThumbnail?: any;
  cameraTagIds: string[];
  regions: { type: string; gridRoi: string; polygonalRoi?: any[] }[];
  videoUrl?: string;
  liveUrl?: string;
  deviceUrl: string | null;
  deviceLocalUrl: string | null;
  turnUrl?: string;
}

/** @deprecated Use `PosDeviceDto` from `@solinkcorp/callhome` */
export interface PosDeviceEntity {
  id: string;
  deviceId: string;
  name: string;
}

export interface LocationFeatureTypes {
  [key: string]: any;
}

export interface LocationFeaturesTypes {
  [key: string]: LocationFeatureTypes;
}

export enum SiteVisibilityStatus {
  VISIBLE = 'VISIBLE',
  HIDDEN = 'HIDDEN',
  FORCE_HIDDEN = 'FORCE_HIDDEN',
}

export enum SiteStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  VIRTUAL = 'VIRTUAL',
}

/** @deprecated Use `MinimizedSiteDto` from `@solinkcorp/callhome` */
export interface BasicCallhomeSite {
  id: SiteId;
  name: string;
  timezone: string;
  address?: string | null;
  locationTags?: string[];
  externalId?: string;
  geolocation?: GeoPoint;
  status: SiteStatus;
  tags?: string[];
}

/** @deprecated Use `SiteDto` from `@solinkcorp/callhome` */
export interface CallhomeSite {
  id: SiteId;
  cloudId: CloudId;
  resellerId: ResellerId;
  customerId: CustomerId;
  motionAlarmScheduleId?: string | null;
  cloudStoragePackageId?: string | null;
  updatedAt?: number | null;
  deletedAt?: number | null;
  name: string;
  address?: string | null;
  geolocation?: GeoPoint;
  timezone: string;
  externalId?: string;
  tags?: string[];
  locationTags?: string[];
  deviceCount?: number | null;
  cameraCount?: number | null;
  onlineCameraCount?: number | null;
  offlineCameraCount?: number | null;
  visibilityStatus: SiteVisibilityStatus;
  status: SiteStatus;
  devices?: DeviceEntity[];
  cameras: CallhomeCamera[];
  posDevices?: PosDeviceEntity[];
  alarmConfig: CallhomeAlarmConfig;
  alarmContacts: AlarmContactEntity[];
  cameraLinks: CallhomeCameraLink[];
  motionAlarmSchedule?: CallhomeScheduleEntity;
  // Populated only when the request opts in via `includeVerifEye: true`.
  verifEyeGroup?: {
    id: VerifEyeGroupId;
    name: string;
    priority: number;
    type: string;
  } | null;
}

/** @deprecated Use `SiteDto` from `@solinkcorp/callhome` with relational includes */
export interface CallhomeSiteWithRelationalData extends CallhomeSite {
  cameras: CallhomeCamera[];
  devices: DeviceEntity[];
  posDevices: PosDeviceEntity[];
  cameraLinks: CallhomeCameraLink[];
  alarmContacts: AlarmContactEntity[];
  alarmConfig: CallhomeAlarmConfig;
  motionAlarmSchedule: CallhomeScheduleEntity;
}

/** @deprecated Use `SiteDto` from `@solinkcorp/callhome` with camera includes */
export interface CallhomeSiteWithCameras extends CallhomeSite {
  cameras: CallhomeCamera[];
}

export interface CallhomeFloorplan {
  id: string;
  cameras: {
    id: string;
    fov: number;
    direction: number;
    x: number;
    y: number;
  }[];
  floorPlanImageUrl?: string;
  width: number;
  height: number;
}

/** @deprecated Use `ResellerDto` from `@solinkcorp/callhome` */
export interface ResellerEntity {
  id: ResellerId;
  cloudId: CloudId;
  name: string;
  eventServerUrl?: string;
  imageServerUrl?: string;
  checkinInterval?: number;
  noteId?: string;
  email?: string;
  password?: string;
  supportContactInformation?: any;
  softwareVersions?: any;
  isEventNotificationsEnabled?: boolean;
  annotationTypes?: any;
  metrics?: any;
  authSettings?: any;
  visionServerUrl?: string;
  connectSettings?: ConnectSettings;
  digestSettings?: any;
  clientSettings?: any;
  reportSettings?: any;
  defaultUserMetadata?: any;
  defaultUserPreferences?: any;
  defaultUserFeatures?: any;
  relayServerId?: string;
  metadata?: any;
}

/** @deprecated Use `CloudDto` from `@solinkcorp/callhome` */
export interface CloudEntity {
  id: CloudId;
  name: string;
  eventServerUrl: string;
  imageServerUrl: string;
  relayServerId?: string;
  backupBucket?: any;
  vmsBucket?: any;
  checkinInterval: number;
  noteId?: string;
  email?: string;
  password?: string;
  supportContactInformation?: any;
  softwareVersions?: any;
  isEventNotificationsEnabled?: boolean;
  annotationTypes?: any;
  metrics?: any;
  authSettings?: any;
  visionServerUrl?: string;
  connectSettings?: ConnectSettings;
  digestSettings?: any;
  clientSettings?: any;
  reportSettings?: any;
  defaultUserMetadata?: any;
  defaultUserPreferences?: any;
  defaultUserFeatures?: any;
  metadata?: any;
}

/** @deprecated Use `CustomerDto` from `@solinkcorp/callhome` */
export interface CustomerEntity {
  id: CustomerId;
  cloudId: CloudId;
  resellerId: ResellerId;
  relayServerId?: string;
  name: string;
  checkinInterval?: number;
  noteId?: string;
  email?: string;
  password?: string;
  useLocalIpAsPrimary?: boolean;
  supportContactInformation?: any;
  defaultHealthContacts?: any[];
  softwareVersions?: any;
  shareDisclaimer?: string;
  isEventNotificationsEnabled?: boolean;
  annotationTypes?: any;
  eventServerUrl?: string;
  metrics?: any;
  authSettings?: any;
  visionServerUrl?: string;
  connectSettings?: ConnectSettings;
  digestSettings?: any;
  clientSettings?: any;
  reportSettings?: any;
  defaultUserMetadata?: any;
  defaultUserPreferences?: any;
  defaultUserFeatures?: any;
  metadata?: any;
  integrations?: any;
  tagMetadata?: { tagCategories: { id: string; parentId: string; label: string }[] };
  cloudStoragePackageId: string | null;
  /**
   * Customer-level default location features.
   * TODO SL-12707 Phase 3 cleanup: remove `alarms.alarmContactMigrated` (and this
   * field if nothing else needs it) once all customers are migrated to customer-level
   * alarm contacts and the migration flag is no longer read.
   */
  defaultLocationFeatures?: {
    alarms?: {
      alarmContactMigrated?: boolean;
    };
  };
}

export type DeviceType = (typeof DEVICE_TYPE)[keyof typeof DEVICE_TYPE];

/** @deprecated Use `DeviceDto` from `@solinkcorp/callhome` */
export interface DeviceEntity {
  id: DeviceId;
  cloudId: CloudId;
  resellerId: ResellerId;
  customerId: CustomerId;
  siteId: SiteId;
  name: string;
  address?: string;
  overrideName?: string;
  state?: string;
  nextCheckin?: Date;
  lastCheckin?: Date;
  currentSoftwareMajorVersion?: number;
  currentSoftwareVersion?: string;
  ipAddress?: string;
  useLocalIpAsPrimary?: boolean;
  isPushToCloudEnabled?: boolean;
  cloudOnly?: boolean;
  speedTestUploadRateS3?: number;
  latestSpeedTestResults?: any;
  isVirtual?: boolean;
  connectSettings?: ConnectSettings;
  deviceUrl: string | null;
  deviceUrlV2?: string | null;
  deviceLocalUrl: string | null;
  metadata?: any;
  features?: LocationFeaturesTypes;
  turnUrl?: string;
  availablePlaybackMethods?: PlaybackMethod[];
  tpuStatus?: TpuStatus;
  tags?: any;
  status?: string;
  type?: string;
  timezoneId?: string;
  reseller?: ResellerEntity;
  customer?: CustomerEntity;
  cameras?: CameraEntity[];
  posDevices?: PosDeviceEntity[];
  operatingHoursSchedule?: CallhomeScheduleEntity;
}

/** @deprecated Use `CameraDto` from `@solinkcorp/callhome` */
export interface CameraEntity {
  id: CHCameraId;
  cloudId: CloudId;
  resellerId: ResellerId;
  customerId: CustomerId;
  deviceId: DeviceId;
  cameraId: VMSCameraId;
  name: string;
  overrideName?: string;
  vmsName?: string;
  status: string;
  streams?: any[];
  thumbnail?: string;
  access: string;
  ip?: string;
  isMotionEnabled?: boolean;
  ['360Settings']?: any;
  motionParams?: any;
  ai?: any;
  audioParams?: any;
  annotations?: any[];
  isUnplugged?: boolean;
  isOutOfFocus?: boolean;
  motionAlarmScheduleId: string | null;
  motionAlarmSchedule?: CallhomeScheduleEntity;
  createdAt?: Date;
  location?: GeoPoint;
}

/** @deprecated Use `UserRoleDto` from `@solinkcorp/callhome` */
export interface UserRoleEntity {
  id: UserRoleId;
  cloudId: CloudId;
  resellerId?: ResellerId;
  customerId?: CustomerId;
  name: string;
  description?: string;
  ownerType: string;
  defaultUserFeatures?: any;
  defaultUserPreferences?: any;
  defaultUserMetadata?: any;
  impersonatableUserIds?: string[];
}

/** @deprecated Use `LocationGroupDto` from `@solinkcorp/callhome` */
export interface LocationGroupEntity {
  id: SiteGroupId;
  cloudId: string;
  resellerId: string;
  customerId: string;
  name: string;
  locationIds?: SiteId[];
  siteIds?: SiteId[];
}

/** @deprecated Use `UserDto` from `@solinkcorp/callhome` */
export interface UserEntity {
  id: UserId;
  email: string;
  name?: string;
  userType: UserType;
  subType: UserSubType | null;
  identityType: UserIdentityType;
  primaryIdentityProvider: string;
  primaryIdentityConnection: string;
  cloudId?: string;
  resellerId?: string;
  customerId?: string;
  deviceId?: string;
  widgetIds?: string[];
  creatorId?: string;
  userRoleId?: string | null;
  cameraIds?: string[];
  locationIds?: string[];
  locationGroupIds?: string[];
  eventTypes?: number;
  metadata?: any;
  preferences?: any;
  features?: any;
  isSiteAccessRestricted?: boolean;
  isSupportAccessEnabled?: boolean;
  supportAccessExpiresAt?: number;
  isDeploymentNotificationsEnabled?: boolean;
  isDigestEnabled?: boolean;
  appMetadata?: any;
  userMetadata?: any;
  lastLogin?: Date;
  createdAt?: Date;
  digestSettings?: any;
  reportSettings?: any;
  revokeTokensIssuedBefore?: Date;
  expiresAt?: Date;
  emailSecret?: string;
  password?: string;
  phoneNumber?: string;
  cloud?: CloudEntity;
  reseller?: ResellerEntity;
  customer?: CustomerEntity;
  device?: DeviceEntity;
  userRole?: UserRoleEntity;
  locationGroups?: LocationGroupEntity[];
}

export const USER_SUBTYPE = {
  PIN_PAD: 'PIN_PAD',
  PARSER: 'PARSER',
  SERVICE: 'SERVICE',
  OWNER: 'OWNER',
  API_KEY: 'API_KEY',
} as const;
export type UserSubType = ValueOf<typeof USER_SUBTYPE>;

export interface GuestPayload {
  cameraIds?: string[];
  email: string;
  emailSubstitutions?: Record<string, unknown>;
  eventTypes?: string;
  expiresAt: Date | string;
  features?: Record<string, unknown>;
  locationIds: string[];
  metadata?: Record<string, unknown>;
  url: string;
  videoPlaybackEndsAt?: string;
  videoPlaybackStartsAt?: string;
}

export enum RsaRuleOwnerType {
  CLOUD = 'CLOUD',
  RESELLER = 'RESELLER',
  CUSTOMER = 'CUSTOMER',
  GLOBAL = 'GLOBAL',
}

/** @deprecated Use `RsaRuleDto` from `@solinkcorp/callhome` */
export interface RsaRuleEntity {
  id: string;
  cloudId?: string;
  resellerId?: string;
  customerId?: string;
  ownerType: RsaRuleOwnerType;
  name: string;
  description?: string;
  filter: Record<string, any>[];
  ai: Record<string, any>;
  tags: string[];
}

export interface EventState {
  nodeId: string;
  node: {
    prompt: string;
    values: string[];
    controlType: 'embedded-buttons' | 'modal-buttons';
    state: 'OPEN' | 'IN_PROGRESS' | 'CLOSED';
    backendAction?: string;
    i18n: {
      en: Record<string, string>;
      es: Record<string, string>;
      fr: Record<string, string>;
    };
  };
  transitions: string[];
  state: 'OPEN' | 'IN_PROGRESS' | 'CLOSED';
  data?: {
    customerId: string;
    transition: string;
  } & (VerificationPrompt | (VerificationClaim & { resetBy?: string; resetAt?: number }));
  timems: number;
}

export type EventStatePayload = { transition?: string } & VerificationDetails;

/** @deprecated Use `EventFlowDto` from `@solinkcorp/callhome` */
export interface EventFlow {
  id: string;
  customerId: string | null;
  ownerType: 'Cloud' | 'Reseller' | 'Customer';
  name: string;
  eventFlowMetadata: Record<string, any>;
}

/** @deprecated Use `AiTagDto` from `@solinkcorp/callhome` */
export interface CallhomeAiTag {
  id: string;
  cloudId: string;
  resellerId: string;
  customerId: string;
  name: string;
}

export interface ServiceDisplayTimeRange {
  /** Military time HH:MM (00:00 – 23:59) */
  start: string;
  /** Military time HH:MM (00:00 – 23:59) */
  end: string;
}

export type ServiceDisplayLayout = components['schemas']['ServiceDisplayLayoutDto'];
export type ServiceDisplayDayPart = components['schemas']['ServiceDisplayDayPartDto'];
export type ServiceDisplayDayParts = components['schemas']['ServiceDisplayDayPartDto']['dayParts'];

export type DayIndex = '0' | '1' | '2' | '3' | '4' | '5' | '6';
/** Sparse record: only include days that have time ranges. Keys "0"–"6" (Sun–Sat). */
export type DayParts = Partial<Record<DayIndex, ServiceDisplayTimeRange[]>>;

export type CHServiceDisplayLayout = components['schemas']['ServiceDisplayLayoutDto'] & {
  widgets?: any[];
};

export type CreateCHServiceDisplayDayPartDto =
  components['schemas']['CreateServiceDisplayDayPartDto'];
export type UpdateCHServiceDisplayDayPartDto = Partial<
  Pick<components['schemas']['ServiceDisplayDayPartDto'], 'label' | 'dayParts'>
>;
